import React, { ChangeEvent, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import 'react-datepicker/dist/react-datepicker.css';
import 'react-phone-number-input/style.css';
import { useTranslation } from 'react-i18next';
import { Field, Form, Formik, FormikErrors, FormikProps } from 'formik';
import CircularProgress from '@mui/material/CircularProgress';

import * as actions from './actions';

import * as Styled from './RXGatePage.styles';
import { useBarcodeScanner } from '../LoyaltyPage/hooks/useBarcodeScanner';
import { GetRecipeInfoPayload } from './types';
import { formatPhoneNumberToBeValid } from '../../global/helpers';
import RecipeInfoBlock from './components/RecipeInfoBlock';

const RXGatePage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [recipe, setRecipe] = useState<string>('');
  const [scanMode, setScanMode] = useState<boolean>(false);

  const normalizeRecipe = useCallback((raw: string) => {
    const beforeDash = raw.split('-')[0];
    return beforeDash.replace(/\D/g, '');
  }, []);

  const onScan = useCallback(
    (text: string) => {
      setRecipe(normalizeRecipe(text));
      setScanMode(false);
    },
    [normalizeRecipe]
  );

  useBarcodeScanner({
    enabled: scanMode,
    onScan,
    endKeys: ['Enter', 'Tab', 'NumpadEnter'],
  });

  const handleInputChange = (
    props: FormikProps<any>,
    event: ChangeEvent<HTMLInputElement>,
    inputName: string
  ) => {
    let { value } = event.target;

    if (inputName === 'phone') {
      value = value.replace(/[^\d+]/g, '');
    }

    if (inputName === 'iin') {
      value = value.replace(/\D/g, '');
    }

    if (inputName === 'recipe') {
      value = normalizeRecipe(value);
    }

    props.setErrors({
      ...props.errors,
      [inputName]: '',
    });

    props.setFieldValue(inputName, value);
  };

  const validateForm = (values: {
    recipe: string;
    iin: string;
    phone: string;
  }) => {
    const { phone, iin, recipe } = values;
    const errors: FormikErrors<GetRecipeInfoPayload> = {};

    if (!phone) {
      errors.phone = t('LoginPage.PHONE_EMPTY_ERROR');
    }
    if (!formatPhoneNumberToBeValid(values.phone)) {
      errors.phone = t('LoginPage.PHONE_INVALID_ERROR');
    }
    if (!iin || iin.length !== 12) {
      errors.iin = t('RXGatePage.IIN_ERROR');
    }
    if (!recipe) {
      errors.recipe = t('RXGatePage.RECIPE_EMPTY_ERROR');
    }
    return errors;
  };

  const submitRecipe = useCallback(
    ({ recipe, iin, phone }: GetRecipeInfoPayload) => {
      dispatch(actions.getRecipeInfo.request({ recipe, iin, phone }));
      dispatch(actions.setRecipeNumber.request(recipe));
    },
    [dispatch]
  );

  return (
    <Styled.RXGatePageContainer>
      <Formik
        initialValues={{
          recipe,
          iin: '',
          phone: '',
        }}
        enableReinitialize
        onSubmit={submitRecipe}
        validate={validateForm}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {(props: FormikProps<any>) => (
          <Form className="form-container">
            <div className="form-block">
              <div className="label">{t('RXGatePage.RECIPE')}</div>
              <Field
                id="recipe"
                name="recipe"
                type="text"
                placeholder="Введите номер рецепта"
                className={`input ${props.errors.recipe && 'input-error'}`}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(props, e, 'recipe')
                }
              />
              <Styled.ScanButton onClick={() => setScanMode((mode) => !mode)}>
                {scanMode ? (
                  <CircularProgress
                    size={20}
                    color="inherit"
                    sx={{ margin: '5px 10px' }}
                  />
                ) : (
                  <p>QR</p>
                )}
              </Styled.ScanButton>
            </div>
            {props.errors.recipe && (
              <p className="validation-error">{props.errors.recipe}</p>
            )}
            <div className="form-block">
              <div className="label">{t('RXGatePage.IIN')}</div>
              <Field
                id="iin"
                name="iin"
                type="number"
                placeholder="Введите ИИН"
                className={`input ${props.errors.iin && 'input-error'}`}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(props, e, 'iin')
                }
              />
            </div>
            {props.errors.iin && (
              <p className="validation-error">{props.errors.iin}</p>
            )}

            <div className="form-block">
              <div className="label">{t('RXGatePage.PHONE')}</div>
              <Field
                id="phone"
                name="phone"
                type="phone"
                placeholder="Введите номер телефона"
                className={`input ${props.errors.phone && 'input-error'}`}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleInputChange(props, e, 'phone')
                }
              />
            </div>
            {props.errors.phone && (
              <p className="validation-error">{props.errors.phone}</p>
            )}

            <Styled.ApplyRecipeButton type="submit">
              {t('RXGatePage.FIND_RECIPE')}
            </Styled.ApplyRecipeButton>
          </Form>
        )}
      </Formik>
      <RecipeInfoBlock />
    </Styled.RXGatePageContainer>
  );
};

export default RXGatePage;
